import './SearchPage.scss';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import { bind } from 'lodash-decorators/bind';
import { getClasses } from 'src/utils/classFunctions';
import { classList } from 'src/utils/classList';
import { RootState } from 'src/state/RootState';
import { connectDecorator } from 'src/decorators/connectDecorator';
import { DEFAULT_GRID_SIZE, GRID_SIZE } from 'src/constants/gridSize';
import { SEARCH_ACTIONS } from 'src/actions/SearchActions';
import { SearchInput } from 'src/components/SearchInput';
import { GridLayout } from 'src/components/GridLayout';
import { HotelCard } from 'src/components/Card/HotelCard';
import { ActivityCard } from 'src/components/Card/ActivityCard';
import { EventCard } from 'src/components/Card/EventCard';
import { WeatherCard } from 'src/components/Card/WeatherCard';
import { SearchNavigation } from 'src/components/SearchNavigation';
import { SearchNavigationTab } from 'src/components/SearchNavigationTab';
import { SearchTitle } from 'src/components/SearchTitle';
import { SearchState } from 'src/state/SearchState';
import { LoadingWrapper } from 'src/components/LoadingWrapper';
import { LoadingSpinner } from 'src/components/LoadingSpinner';
import { SearchResult, SearchResultSectionType } from 'src/state/SearchResult';
import { getSuggestions } from 'src/selectors/getSuggestions';
import { CardAppear } from 'src/components/CardAppear';
import { ShoppingCard } from 'src/components/Card/ShoppingCard';
import { WebcamCard } from 'src/components/Card/WebcamCard';
import { SeeAllButton } from 'src/components/SeeAllButton';
import { SearchResultItem } from '@xiag-search/sdk';
import { getCurrentQuery } from 'src/selectors/getCurrentQuery';
import { SUGGEST_ACTIONS } from 'src/actions/SuggestActions';
import { DefaultPromo } from 'src/components/Card/DefaultPromo';
import { DefaultBooking } from 'src/components/Card/DefaultBooking';
import { DefaultMini } from 'src/components/Card/DefaultMini';
import { OfferCard } from 'src/components/Card/OfferCard';

type SectionData = {
    readonly sectionType: SearchResultSectionType;
    readonly searchItems: ReadonlyArray<SearchResult>;
};

type OwnProps = {
    readonly modificators?: ReadonlyArray<string>;
};
type StateProps = {
    readonly query: string;
    readonly state: SearchState;
    readonly suggestions: ReadonlyArray<string>
};
type DispatchProps = {
    readonly updateSearchData: (query: string) => void;
    readonly updateSuggestData: (query: string) => void;
};
type AllProps =
    & OwnProps
    & StateProps
    & DispatchProps;

class SearchPageConnected extends React.Component<AllProps> {
    public render(): React.ReactNode {
        const { query, suggestions, modificators = [] } = this.props;

        return (
            <div className={classList(getClasses('lvo-search-page', modificators))}>
                <div className="lvo-search-page__wrapper">
                    <div className="lvo-search-page__search-wrapper">
                        <SearchInput value={query}
                                     total={this.getTotalItemCount()}
                                     suggestions={suggestions}
                                     onChange={this.handleSearchChange}
                                     onSubmit={this.handleSearchSubmit}
                                     modificators={this.props.query ? [] : ['no-amount']}/>
                    </div>
                    {this.renderResult()}
                </div>
            </div>
        );
    }

    private renderResult(): JSX.Element | null {
        const { state: { error, loading } } = this.props;
        if (error) {
            return null;
        }

        return (
            <LoadingWrapper loading={loading}>
                {this.renderFounds()}
            </LoadingWrapper>
        );
    }

    private renderFounds(): JSX.Element | null {
        const { state: { data } } = this.props;
        if (!data) {
            return null;
        }

        if (data.results.length === 0) {
            return this.renderNoResults();
        }

        if (!this.props.query) {
            return this.renderEmptyQueryGrid();
        }

        return (
            <div className="lvo-search-page__results-wrapper">
                {this.renderGrid()}
                {this.renderAdditionsResults()}
            </div>
        );
    }

    private renderNoResults(): JSX.Element {
        return (
            <div className="g-container">
                <div className="lvo-search-page__no-results">
                    <FormattedMessage id="searchPage_noResultsText" values={{
                        phone: <a href="tel:+41818300001">+41 81 830 00 01</a>,
                    }}/>
                </div>
            </div>
        );
    }

    private renderAdditionsResults(): JSX.Element | null {
        const { state: { data, params } } = this.props;
        if (!data) {
            return null;
        }

        if (!data.isFullyLoaded) {
            return (
                <div className="lvo-search-page__results-more">
                    <LoadingSpinner/>
                </div>
            );
        }

        return (
            <div className="lvo-search-page__results-more">
                {this.renderNavigation()}

                {params.section
                    ? this.renderSectionResults(params.section)
                    : this.renderAllResults()}
            </div>
        );
    }

    private renderNavigation(): JSX.Element | null {
        const { state: { data, params } } = this.props;
        if (data === null) {
            return null;
        }

        return (
            <SearchNavigation results={data.total}>
                <SearchNavigationTab key="all"
                                     params={params}
                                     section={null}>
                    <FormattedMessage id="searchPage_sectionTab" values={{
                        count: data.total,
                        title: (
                            <b>
                                <FormattedMessage id="searchPage_sectionName"
                                                  values={{ section: 'all' }}/>
                            </b>
                        ),
                    }}/>
                </SearchNavigationTab>
                {this.getDataSections()
                    .filter(({ searchItems }) => searchItems.length > 0)
                    .map(({ sectionType }) => (
                        <SearchNavigationTab key={sectionType}
                                             params={params}
                                             section={sectionType}>
                            <FormattedMessage id="searchPage_sectionTab" values={{
                                count: this.getSectionItemsCount(sectionType),
                                title: (
                                    <b>
                                        <FormattedMessage id="searchPage_sectionName"
                                                          values={{ section: sectionType }}/>
                                    </b>
                                ),
                            }}/>
                        </SearchNavigationTab>
                    ))}
            </SearchNavigation>
        );
    }

    private renderEmptyQueryGrid(): JSX.Element | null {
        const { state: { data } } = this.props;

        if (!data || !data.isFullyLoaded) {
            return null;
        }

        if (!data || data.results.length === 0) {
            return null;
        }

        const gridItems = data.results
            .filter((it) => this.displayGridFilter(it.searchItem))
            .slice(0, DEFAULT_GRID_SIZE);

        const { state: { params: { query, section } } } = this.props;
        const gridLayout = DEFAULT_GRID_LAYOUT_PARAMETERS[DEFAULT_GRID_SIZE];
        return (
            <GridLayout key={query}
                        hidden={section !== null}
                        modificators={gridLayout.gridModificators}>
                {gridItems.map((item, index) => {
                    const gridPosition = gridLayout.itemGridPositions[index % DEFAULT_GRID_SIZE];
                    return (
                        <CardAppear key={item.id}
                                    delay={index * 100}
                                    className={gridPosition}>
                            {this.renderDefaultScreenItem(item, index)}
                        </CardAppear>
                    );
                })}
            </GridLayout>
        );
    }

    private renderAllResults(): JSX.Element[] {
        const designVariant = '1';
        const nodeModificator = 'XL';

        const { state: { params: { query } } } = this.props;

        return this.getDataSections()
            .filter(({ searchItems }) => searchItems.length > 0)
            .map(({ sectionType, searchItems }) => (
                <div key={sectionType}>
                    <SearchTitle keyword={this.getSectionItemsCount(sectionType).toString()}>
                        <FormattedMessage id="searchPage_sectionName"
                                          values={{ section: sectionType }}/>
                    </SearchTitle>
                    <div className="g-container">
                        <div className="g-row">
                            {searchItems.slice(0, 3).map((item) => (
                                <div key={item.id}
                                     className="g-col-md-2 g-col-sm-2 g-col-xl-4 g-col-lg-2 section-item">
                                    {this.renderItem(item, `design-${designVariant}`, nodeModificator, 'design-2')}
                                </div>
                            ))}
                        </div>
                    </div>
                    {searchItems.length > 3 && (
                        <div className="lvo-search-page__see-all">
                            <div className="g-container">
                                <div className="g-row">
                                    <div className="g-col-md-4 g-col-sm-2 g-col-xl-4 g-col-lg-6 lvo-search-page__see-all-wrapper">
                                        <SeeAllButton count={this.getSectionItemsCount(sectionType)}
                                                      query={query}
                                                      section={sectionType}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ));
    }

    private renderSectionResults(sectionType: SearchResultSectionType): JSX.Element | null {
        const { state: { data } } = this.props;
        if (!data || !data.isFullyLoaded) {
            return null;
        }

        const designVariant = '1';
        const nodeModificator = 'XL';

        const sectionItems = data.results.filter((it) =>
            it.sectionType === sectionType && this.displayFilter(it.searchItem));

        return (
            <div>
                <SearchTitle keyword={sectionItems.length.toString()}>
                    <FormattedMessage id="searchPage_sectionName"
                                      values={{ section: sectionType }}/>
                </SearchTitle>
                <div className="g-container">
                    <div className="g-row">
                        {sectionItems.map((item) => (
                            <div key={item.id}
                                 className="g-col-md-2 g-col-sm-2 g-col-xl-4 g-col-lg-2 section-item">
                                {this.renderItem(
                                    item,
                                    `design-${designVariant}`,
                                    nodeModificator,
                                    'design-2',
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    private renderGrid(): JSX.Element | null {
        const { state: { data } } = this.props;
        if (!data || data.results.length === 0) {
            return null;
        }

        const gridItems = data.results
            .filter((it) => this.displayGridFilter(it.searchItem))
            .slice(0, GRID_SIZE);
        const gridLayout = GRID_LAYOUT_PARAMETERS[gridItems.length];

        const { state: { params: { query, section } } } = this.props;

        return (
            <GridLayout key={query}
                        hidden={section !== null}
                        modificators={gridLayout.gridModificators}>
                {gridItems.map((item, index) => {
                    const designVariant = gridLayout.itemModificators[index] === 'XL' ? '2' : '1';
                    const gridPosition = gridLayout.itemGridPositions[index];
                    const nodeModificator = gridLayout.itemModificators[index];

                    return (
                        <CardAppear key={item.id}
                                    delay={index * 100}
                                    className={gridPosition}>
                            {this.renderItem(
                                item,
                                `design-${designVariant}`,
                                nodeModificator,
                                `design-${designVariant}`,
                            )}
                        </CardAppear>
                    );
                })}
            </GridLayout>
        );
    }

    private renderItem(
        item: SearchResult,
        variant: string,
        modificator: string,
        weatherDefaultVariant: string,
    ): React.ReactElement {
        switch (item.displayType) {
            case 'hotel':
            case 'fewo':
                return (
                    <HotelCard key={item.id}
                               variant={variant}
                               modificators={[modificator]}
                               searchItem={item}/>
                );

            case 'event':
                return (
                    <EventCard key={item.id}
                               variant={variant}
                               modificators={[modificator]}
                               searchItem={item}/>
                );

            case 'weather':
                return (
                    <WeatherCard key={item.id}
                                 variant={item.designVariant ? variant : weatherDefaultVariant}
                                 modificators={[modificator]}
                                 searchItem={item}/>
                );

            case 'shopping':
                return (
                    <ShoppingCard key={item.id}
                                  variant={variant}
                                  modificators={[modificator]}
                                  searchItem={item}/>
                );

            case 'webcam':
                return (
                    <WebcamCard key={item.id}
                                variant={variant}
                                modificators={[modificator]}
                                searchItem={item}/>
                );

            case 'offer':
                return (
                    <OfferCard key={item.id}
                               searchItem={item}
                               variant={variant}
                               modificators={[modificator]}
                    />
                );

            default:
                return (
                    <ActivityCard key={item.id}
                                  variant={variant}
                                  modificators={[modificator]}
                                  searchItem={item}/>
                );
        }
    }

    private renderDefaultScreenItem(item: SearchResult, index: number): React.ReactElement {
        if (index === 0) {
            return (
                <DefaultPromo
                    searchItem={item}
                    key={item.id}/>
            );
        }
        if (index > 0 && index < 3) {
            return (
                <DefaultBooking
                    searchItem={item}
                    key={item.id}/>
            );
        }
        return (
            <DefaultMini
                searchItem={item}
                key={item.id}/>
        );
    }

    private getDataSections(): SectionData[] {
        const { state: { data } } = this.props;
        if (!data || data.results.length === 0) {
            return [];
        }

        const sections: ReadonlyArray<SearchResultSectionType> = [
            'activities',
            'offers',
            'accommodations',
            'events',
            'shopping',
            'information',
        ];
        return sections.map((sectionType) => ({
            sectionType,
            searchItems: data.results.filter((item) =>
                item.sectionType === sectionType && this.displayFilter(item.searchItem)),
        }));
    }

    private displayGridFilter(item: SearchResultItem): boolean {
        return !(!this.displayFilter(item) && !item.isPinned);
    }

    private displayFilter(item: SearchResultItem): boolean {
        const isExclude = typeof item.customFields['exclude-result'] === 'boolean'
            ? item.customFields['exclude-result']
            : false;
        return !isExclude;
    }

    private getSectionItemsCount(sectionType: SearchResultSectionType): number {
        const { state: { data } } = this.props;
        return data
            ? data.results.filter((it) =>
                it.sectionType === sectionType && this.displayFilter(it.searchItem)).length
            : 0;
    }

    private getTotalItemCount(): number | null {
        const { state: { data } } = this.props;
        return data === null ? null : data.total;
    }

    @bind()
    private handleSearchChange(query: string): void {
        const { updateSuggestData } = this.props;
        updateSuggestData(query);
    }

    @bind()
    private handleSearchSubmit(query: string): void {
        const { updateSearchData } = this.props;
        updateSearchData(query);
    }
}

type GridLayoutParameters = {
    readonly gridModificators: ReadonlyArray<string>;
    readonly itemModificators: ReadonlyArray<string>;
    readonly itemGridPositions: ReadonlyArray<string>;
};
const GRID_LAYOUT_PARAMETERS: Record<number, GridLayoutParameters> = {
    5: {
        gridModificators: ['large'],
        itemModificators: ['XXL', 'XL', 'L', 'M', 'M'],
        itemGridPositions: ['Place-1', 'Place-2', 'Place-3', 'Place-4', 'Place-5'],
    },
    4: {
        gridModificators: ['medium'],
        itemModificators: ['XL', 'L', 'M', 'M'],
        itemGridPositions: ['Place-2', 'Place-3', 'Place-4', 'Place-5'],
    },
    3: {
        gridModificators: ['small'],
        itemModificators: ['M', 'M', 'M'],
        itemGridPositions: ['Place-4', 'Place-5', 'Place-6'],
    },
    2: {
        gridModificators: ['small'],
        itemModificators: ['M', 'M'],
        itemGridPositions: ['Place-4', 'Place-5'],
    },
    1: {
        gridModificators: ['small'],
        itemModificators: ['M'],
        itemGridPositions: ['Place-4'],
    },
};

const DEFAULT_GRID_LAYOUT_PARAMETERS: Record<number, GridLayoutParameters> = {
    8: {
        gridModificators: ['default'],
        itemModificators: [],
        itemGridPositions: ['Place-1', 'Place-2', 'Place-3', 'Place-4', 'Place-5', 'Place-6', 'Place-7', 'Place-8'],
    },
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
    state: (state) => state.search,
    query: getCurrentQuery,
    suggestions: getSuggestions,
});

const mapDispatchToProps: DispatchProps = {
    updateSearchData: (query) => SEARCH_ACTIONS.request(undefined, { query, section: null }),
    updateSuggestData: (query) => SUGGEST_ACTIONS.request(undefined, query),
};

@connectDecorator<OwnProps, StateProps, DispatchProps>(SearchPageConnected, mapStateToProps, mapDispatchToProps)
export class SearchPage extends React.Component<OwnProps> {}
